import reportjobordercustomerlocationService from '../../../services/reportjobordercustomerlocationService';
import { loadDeferredTotal } from '../../../utils/deferredTotal';
import filterService from '../../../services/filterService';

import {
    setReportjobordercustomerlocationLoading,
    setReportjobordercustomerlocationError,
    setReportjobordercustomerlocationList,
    setReportjobordercustomerlocationCurrent,
    setReportjobordercustomerlocationPagination,
    setReportjobordercustomerlocationSort,
    setReportjobordercustomerlocationFilters,
    setReportjobordercustomerlocationDefaultFilter,
    setReportjobordercustomerlocationSettings,
    clearReportjobordercustomerlocationCurrent,
    clearReportjobordercustomerlocationError,
    setReportjobordercustomerlocationFilterLoading,
    changeReportjobordercustomerlocationFilterCondition,
    addReportjobordercustomerlocationFilterCondition,
    updateReportjobordercustomerlocationFilterConditionRule,
    updateReportjobordercustomerlocationFilterConditionValue,
    removeReportjobordercustomerlocationFilterCondition,
    setReportjobordercustomerlocationSelected,
    setReportjobordercustomerlocationDownloading,
    resetReportjobordercustomerlocationFiltersAndPagination,

    setReportjobordercustomerlocationClassicFilters
} from './reportjobordercustomerlocationSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().reportjobordercustomerlocation.isSettingsInitialized) {
            dispatch(setReportjobordercustomerlocationLoading(true));
            const response = await reportjobordercustomerlocationService.getSettings();

            // Set the settings
            dispatch(setReportjobordercustomerlocationSettings(response));

            const classicFilters = response?.classic_filters || [];
            dispatch(setReportjobordercustomerlocationClassicFilters(classicFilters));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setReportjobordercustomerlocationDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setReportjobordercustomerlocationError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setReportjobordercustomerlocationLoading(false));
    }
};

export const fetchReportjobordercustomerlocations = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().reportjobordercustomerlocation;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setReportjobordercustomerlocationLoading(true));
        const response = await reportjobordercustomerlocationService.getList(finalQueryParams, finalActiveFilter, state.classicFilters);
        dispatch(setReportjobordercustomerlocationList(response));
        loadDeferredTotal(response, reportjobordercustomerlocationService, finalQueryParams, finalActiveFilter, state.classicFilters, dispatch, setReportjobordercustomerlocationPagination);
        return response;
    } catch (error) {
        console.error('Error in fetchReportjobordercustomerlocations:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setReportjobordercustomerlocationError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportjobordercustomerlocationLoading(false));
    }
};

export const fetchReportjobordercustomerlocation = (id) => async (dispatch) => {
    try {
        dispatch(setReportjobordercustomerlocationLoading(true));
        const response = await reportjobordercustomerlocationService.getOne(id);
        dispatch(setReportjobordercustomerlocationCurrent(response.data));
    } catch (error) {
        dispatch(setReportjobordercustomerlocationError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setReportjobordercustomerlocationLoading(false));
    }
};

export const createReportjobordercustomerlocation = (data) => async (dispatch) => {
    try {
        const response = await reportjobordercustomerlocationService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateReportjobordercustomerlocation = (id, data) => async (dispatch) => {
    try {
        const response = await reportjobordercustomerlocationService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteReportjobordercustomerlocation = (id) => async (dispatch) => {
    try {
        dispatch(setReportjobordercustomerlocationLoading(true));
        const response = await reportjobordercustomerlocationService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setReportjobordercustomerlocationError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportjobordercustomerlocationLoading(false));
    }
};

export const downloadReportjobordercustomerlocation = (type) => async (dispatch, getState) => {
    try {
        const state = getState().reportjobordercustomerlocation;

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            export_type: type
        };

        const filter = state.defaultFilter;
        dispatch(setReportjobordercustomerlocationDownloading(true));
        const response = await reportjobordercustomerlocationService.downloadAsync(finalQueryParams, filter, state.classicFilters);
        dispatch(setReportjobordercustomerlocationDownloading(false));

        return response;
    } catch (error) {
        console.error('Error downloading reportjobordercustomerlocations:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setReportjobordercustomerlocationError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReportjobordercustomerlocationDownloading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setReportjobordercustomerlocationPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setReportjobordercustomerlocationSort(sort));
};

export const searchReportjobordercustomerlocations = (query) => (dispatch, getState) => {
    const currentFilters = getState().reportjobordercustomerlocation.filters;
    dispatch(setReportjobordercustomerlocationFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setReportjobordercustomerlocationDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeReportjobordercustomerlocationFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addReportjobordercustomerlocationFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateReportjobordercustomerlocationFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateReportjobordercustomerlocationFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeReportjobordercustomerlocationFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setReportjobordercustomerlocationFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().reportjobordercustomerlocation.settings;
            dispatch(setReportjobordercustomerlocationSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setReportjobordercustomerlocationFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setReportjobordercustomerlocationFilterLoading(false));
        dispatch(setReportjobordercustomerlocationError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setReportjobordercustomerlocationSelected(selectedRows));
    };
};

export const clearCurrentReportjobordercustomerlocation = () => {
    return (dispatch) => {
        dispatch(clearReportjobordercustomerlocationCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetReportjobordercustomerlocationFiltersAndPagination());
};

export const changeClassicFilter = (name, value) => (dispatch, getState) => {
    const currentClassicFilters = getState().reportjobordercustomerlocation.classicFilters;

    // Create a deep copy of the classic filters
    const updatedClassicFilters = { ...currentClassicFilters };
    
    // Update the nested value in the query object
    if (updatedClassicFilters[name]) {
        updatedClassicFilters[name] = {
            ...updatedClassicFilters[name],
            query: {
                ...updatedClassicFilters[name].query,
                ...(typeof value === 'object' && value !== null ? value : { value })
            }
        };
    }

    dispatch(setReportjobordercustomerlocationClassicFilters(updatedClassicFilters));
};